import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ComponentFrameworkFrameworkItem, ComponentFrameworkFrameworkItemBox } from '@edwin-group/cj-graphql';
import { Maybe } from '@/graphql/generated/graphql';

export type RespectAccordionProps = {
  frameworkItems: ComponentFrameworkFrameworkItem[];
  allowMultiple?: boolean;
  defaultOpenIndex?: number;
  className?: string;
};

export default function RespectAccordion({
  frameworkItems,
  allowMultiple = false,
  defaultOpenIndex = -1,
  className = '',
}: RespectAccordionProps) {
  const [openIndices, setOpenIndices] = useState<number[]>(
    typeof defaultOpenIndex === 'number' && defaultOpenIndex >= 0 && defaultOpenIndex < frameworkItems.length
      ? [defaultOpenIndex]
      : [],
  );

  const toggleIndex = useCallback(
    (idx: number) => {
      const isOpen = openIndices.includes(idx);
      if (allowMultiple) {
        setOpenIndices(isOpen ? openIndices.filter((i) => i !== idx) : [...openIndices, idx]);
      } else {
        setOpenIndices(isOpen ? [] : [idx]);
      }
    },
    [allowMultiple, openIndices],
  );

  return (
    <div className={'mt-4 space-y-2 overflow-hidden bg-white ' + className}>
      {frameworkItems.map((item, idx) => {
        const title = item?.title ?? '';
        const letter = title.slice(0, 1).toUpperCase();
        const color = item?.color ?? '#D0D5DD';
        const isOpen = openIndices.includes(idx);
        const description = item?.description ?? '';
        const boxes = item?.frameworkItemBox ?? [];

        return (
          <TraitAccordionItem
            key={`${title}-${idx}`}
            idx={idx}
            title={title}
            letter={letter}
            color={color}
            description={description}
            boxes={boxes}
            isOpen={isOpen}
            onToggle={() => toggleIndex(idx)}
          />
        );
      })}
    </div>
  );
}

function TraitAccordionItem({
  idx,
  title,
  letter,
  color,
  description,
  boxes,
  isOpen,
  onToggle,
}: {
  idx: number;
  title: string;
  letter: string;
  color: string;
  description: string;
  boxes: Maybe<ComponentFrameworkFrameworkItemBox>[];
  isOpen: boolean;
  onToggle: () => void;
}) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [measured, setMeasured] = useState(0);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const update = () => setMeasured(el.scrollHeight + 1);
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => ro.disconnect();
  }, [description, boxes]);

  const contentId = useMemo(() => `accordion-panel-${idx}-${title.replace(/\s+/g, '-').toLowerCase()}`, [idx, title]);

  return (
    <div className="">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={contentId}
        className="flex w-full items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2">
        <div className="flex h-16 w-10 shrink-0 items-center justify-center" style={{ backgroundColor: color }}>
          <span className="text-sh-28-32 font-bold leading-none text-black-100">{letter}</span>
        </div>
        <div
          className={[
            'flex h-16 flex-1 items-center border border-[#bfbfbf] pl-4',
            isOpen ? 'border-b-0' : 'border-b',
            'text-black-100',
          ].join(' ')}>
          <span className="mr-auto text-sh-22-28 font-bold">{title}</span>
          {isOpen ? (
            <XMarkIcon className="mr-3 h-8 w-8 text-primary-200" />
          ) : (
            <ChevronRightIcon className="mr-3 h-8 w-8 text-primary-200" />
          )}
        </div>
      </button>

      <div
        id={contentId}
        aria-hidden={!isOpen}
        className="overflow-hidden"
        style={{
          maxHeight: isOpen ? measured : 0,
          opacity: isOpen ? 1 : 0,
          transition: 'max-height 180ms cubic-bezier(0.33,1,0.68,1), opacity 140ms linear',
        }}>
        <div className="relative">
          <div className="absolute bottom-0 left-0 top-0 w-10" style={{ backgroundColor: color }} />
          <div
            ref={contentRef}
            className={['ml-10 space-y-2 border border-[#bfbfbf] px-4 pb-4', isOpen ? 'border-t-0' : 'border-t'].join(
              ' ',
            )}>
            {description?.length ? <p className="text-sh-14-20 text-black">{description}</p> : null}

            {boxes?.length ? (
              <div className="space-y-3">
                {boxes.map((b, i) => (
                  <Card key={`${b?.title ?? ''}-${i}`} title={b?.title ?? ''} text={b?.description ?? ''} />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ title, text }: { title?: string; text?: string }) {
  return (
    <div className="rounded-2xl border border-[#9fc3fa] p-3" style={{ backgroundColor: '#EAF2FF' }}>
      {title ? <h4 className="mb-1 text-sh-22-28 font-bold text-black-100">{title}</h4> : null}
      {text ? <p className="text-sh-14-20 text-black">{text}</p> : null}
    </div>
  );
}
